import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import krx001Cover from "@/assets/krx001.jpg";

const STATUS_SEQUENCE = ["STANDBY", "INITIALIZING", "LAUNCH IMMINENT"];
const STATUS_INTERVAL_MS = 3600;

const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
    .join("&");

const Landing = () => {
  const [statusIndex, setStatusIndex] = useState(0);
  const [coverOpen, setCoverOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);

  // Promo form state
  const [promoEmail, setPromoEmail] = useState("");
  const [promoArtist, setPromoArtist] = useState("");
  const [promoBot, setPromoBot] = useState(""); // honeypot
  const [promoStartedAt] = useState(() => Date.now());
  const [promoSubmitting, setPromoSubmitting] = useState(false);
  const [promoSubmitted, setPromoSubmitted] = useState(false);
  const [promoError, setPromoError] = useState("");

  useEffect(() => {
    const id = setInterval(() => {
      setStatusIndex((i) => (i + 1) % STATUS_SEQUENCE.length);
    }, STATUS_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Lock body scroll when any overlay is open + Esc to close
  useEffect(() => {
    const anyOpen = coverOpen || promoOpen;
    if (!anyOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setCoverOpen(false);
        setPromoOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [coverOpen, promoOpen]);

  const closePromo = () => {
    setPromoOpen(false);
    // Reset after close animation timing
    setTimeout(() => {
      setPromoSubmitted(false);
      setPromoError("");
      setPromoEmail("");
      setPromoArtist("");
      setPromoBot("");
    }, 250);
  };

  const handlePromoSubmit = async (e) => {
    e.preventDefault();
    setPromoError("");

    // Honeypot — silently succeed for bots
    if (promoBot) {
      setPromoSubmitted(true);
      return;
    }

    // Time-trap: real users take >1.2s to fill the form
    if (Date.now() - promoStartedAt < 1200) {
      setPromoSubmitted(true);
      return;
    }

    // Basic email validation
    const email = promoEmail.trim();
    const artist = promoArtist.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setPromoError("Please enter a valid email address.");
      return;
    }
    if (artist.length < 2) {
      setPromoError("Please enter your artist / DJ name.");
      return;
    }

    setPromoSubmitting(true);
    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "promo-pool",
          email,
          artist,
          "bot-field": "",
        }),
      });
      if (!res.ok) throw new Error("Network error");
      setPromoSubmitted(true);
    } catch (err) {
      setPromoError("Submission failed. Please try again in a moment.");
    } finally {
      setPromoSubmitting(false);
    }
  };

  return (
    <main
      data-testid="kordrax-landing"
      className="kordrax-root"
    >
      <div
        data-testid="kordrax-scanline"
        className="kordrax-scanline"
        aria-hidden="true"
      />
      <div className="kordrax-content">
        <h1
          data-testid="kordrax-title"
          className="kordrax-title"
          aria-label="KORDRAX"
        >
          KORDRAX
        </h1>

        <div data-testid="kordrax-status" className="kordrax-status">
          <span
            data-testid="kordrax-status-dot"
            className="kordrax-status-dot"
            aria-hidden="true"
          />
          <span
            key={STATUS_SEQUENCE[statusIndex]}
            data-testid="kordrax-status-text"
            className="kordrax-status-text"
            aria-live="polite"
          >
            STATUS : {STATUS_SEQUENCE[statusIndex]}
          </span>
        </div>

        <p data-testid="kordrax-tagline" className="kordrax-tagline">
          A NEW FREQUENCY EMERGES FROM THE VOID
        </p>

        <figure
          data-testid="kordrax-release-card"
          className="kordrax-release-card"
        >
          <button
            type="button"
            data-testid="kordrax-release-art-button"
            className="kordrax-release-art-wrap"
            onClick={() => setCoverOpen(true)}
            aria-label="View KRX//001 cover full size"
          >
            <img
              data-testid="kordrax-release-art"
              className="kordrax-release-art"
              src={krx001Cover}
              alt="KORDRAX KRX//001 — Jona V — No Signal"
              loading="lazy"
              decoding="async"
            />
            <span className="kordrax-release-art-hint" aria-hidden="true">
              VIEW
            </span>
          </button>
          <figcaption className="kordrax-release-meta">
            <span className="kordrax-release-meta-line">JONA V — NO SIGNAL</span>
            <span className="kordrax-release-meta-sub">
              KRX//001 · COMING SOON
            </span>
          </figcaption>
        </figure>
      </div>

      <div data-testid="kordrax-footer" className="kordrax-footer">
        <a
          data-testid="kordrax-instagram"
          className="kordrax-instagram"
          href="https://www.instagram.com/kordrax.records/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="KORDRAX on Instagram"
        >
          <svg
            className="kordrax-instagram-icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="3" width="18" height="18" rx="5" ry="5" />
            <path d="M16.5 11.4A4.5 4.5 0 1 1 12 7a4.5 4.5 0 0 1 4.5 4.4z" />
            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
          </svg>
        </a>

        <span className="kordrax-footer-sep" aria-hidden="true" />

        <button
          type="button"
          data-testid="kordrax-promo-trigger"
          className="kordrax-promo-trigger"
          onClick={() => setPromoOpen(true)}
        >
          ENTER OUR PROMO POOL
        </button>
      </div>

      {coverOpen && (
        <div
          data-testid="kordrax-cover-lightbox"
          className="kordrax-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="KRX//001 cover"
          onClick={() => setCoverOpen(false)}
        >
          <button
            type="button"
            data-testid="kordrax-cover-close"
            className="kordrax-lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              setCoverOpen(false);
            }}
            aria-label="Close cover"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
          <img
            data-testid="kordrax-cover-full"
            className="kordrax-lightbox-img"
            src={krx001Cover}
            alt="KORDRAX KRX//001 — Jona V — No Signal"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {promoOpen && (
        <div
          data-testid="kordrax-promo-modal"
          className="kordrax-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="kordrax-promo-title"
          onClick={closePromo}
        >
          <div
            className="kordrax-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              data-testid="kordrax-promo-close"
              className="kordrax-modal-close"
              onClick={closePromo}
              aria-label="Close promo pool form"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>

            {!promoSubmitted ? (
              <>
                <h2 id="kordrax-promo-title" className="kordrax-modal-title">
                  PROMO POOL
                </h2>
                <p className="kordrax-modal-subtitle">
                  Get every KORDRAX release before it drops. DJs, press &amp;
                  industry only.
                </p>

                <form
                  className="kordrax-promo-form"
                  name="promo-pool"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  onSubmit={handlePromoSubmit}
                  noValidate
                >
                  <input type="hidden" name="form-name" value="promo-pool" />

                  {/* Honeypot — invisible to humans, bots will fill it */}
                  <label className="kordrax-honeypot" aria-hidden="true">
                    Don't fill this out if you're human:
                    <input
                      name="bot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      value={promoBot}
                      onChange={(e) => setPromoBot(e.target.value)}
                    />
                  </label>

                  <label className="kordrax-field">
                    <span className="kordrax-field-label">EMAIL</span>
                    <input
                      data-testid="kordrax-promo-email"
                      className="kordrax-field-input"
                      type="email"
                      name="email"
                      required
                      autoComplete="email"
                      placeholder="you@domain.com"
                      value={promoEmail}
                      onChange={(e) => setPromoEmail(e.target.value)}
                    />
                  </label>

                  <label className="kordrax-field">
                    <span className="kordrax-field-label">ARTIST / DJ NAME</span>
                    <input
                      data-testid="kordrax-promo-artist"
                      className="kordrax-field-input"
                      type="text"
                      name="artist"
                      required
                      autoComplete="name"
                      placeholder="Your name"
                      value={promoArtist}
                      onChange={(e) => setPromoArtist(e.target.value)}
                    />
                  </label>

                  {promoError && (
                    <p
                      data-testid="kordrax-promo-error"
                      className="kordrax-form-error"
                      role="alert"
                    >
                      {promoError}
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="kordrax-promo-submit"
                    className="kordrax-submit-btn"
                    disabled={promoSubmitting}
                  >
                    {promoSubmitting ? "SUBMITTING…" : "REQUEST ACCESS"}
                  </button>
                </form>
              </>
            ) : (
              <div
                data-testid="kordrax-promo-success"
                className="kordrax-promo-success"
              >
                <h2 className="kordrax-modal-title">SIGNAL RECEIVED</h2>
                <p className="kordrax-modal-subtitle">
                  You're on the list. We'll be in touch before the next drop.
                </p>
                <button
                  type="button"
                  className="kordrax-submit-btn"
                  onClick={closePromo}
                >
                  CLOSE
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
